import { useCallback, useEffect, useState, FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiSend } from '@/lib/api';
import { formatCalendarDay, formatInstant } from '@/lib/dates';
import { flightTimeProblem } from '@/lib/flightPatch';
import { distanceLabel, hoursHint, unloggedView, type AircraftSummary, type RouteFacts } from '@/lib/summary';
import { PageHeader, Card, Spinner, EmptyState, ErrorNote, inputClass } from '@/components/ui';

/**
 * The co-owner's flying, and whether every flight FlightAware saw is logged.
 *
 * This page used to be Reconciliation: it tied the log to the Hobbs meter. The
 * log moved off engine time onto typed flight time and the balance went with it
 * (the owner, 2026-09-24), so what is left are the two jobs that never depended on
 * the meter:
 *
 *   1  is every flight logged?   detections nobody has classified, counted — zero included
 *   2  the co-owner's flying     recorded for the aircraft's history and BA Law's
 *                                whole-aircraft sheet, each leg with its flight time
 *
 * A leg both owners flew is ONE row in the flight log at the whole leg's flight
 * time; nothing here splits it. Every figure comes from the API.
 */

interface OtherFlight extends RouteFacts {
  id: string;
  date: string;
  departure_airport: string | null;
  arrival_airport: string | null;
  /** Flight time as entered; `null` until somebody enters one. */
  flight_hours: number | null;
  note: string | null;
  source: string;
  /** FlightAware's airborne time for the detection it was flagged from — the prefill. */
  airborne_hours: number | null;
}

interface Detected {
  id: string;
  departure_airport: string;
  arrival_airport: string;
  departure_time: string;
  flight_time_hours?: number | null;
}

const today = () => new Date().toISOString().slice(0, 10);
const hrs = (n: number) => `${n.toFixed(2)} h`;
const code = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '');

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Card className="p-5 sm:p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">{title}</h2>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

export default function CoOwner() {
  const [summary, setSummary] = useState<AircraftSummary | null>(null);
  const [pending, setPending] = useState<Detected[]>([]);
  const [others, setOthers] = useState<OtherFlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [of, setOf] = useState({ date: today(), dep: '', arr: '', hours: '', note: '' });
  const [edit, setEdit] = useState<{ id: string; date: string; dep: string; arr: string; hours: string; note: string } | null>(null);

  const load = useCallback(() => {
    Promise.all([
      apiGet<AircraftSummary>('/api/summary'),
      apiGet<{ flights: Detected[] }>('/api/flightaware/unmatched'),
      apiGet<{ flights: OtherFlight[] }>('/api/other-flights'),
    ])
      .then(([s, u, o]) => {
        setSummary(s);
        setPending(u.flights || []);
        setOthers(o.flights || []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the co-owner page'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  async function addOther(e: FormEvent) {
    e.preventDefault();
    const problem = flightTimeProblem(of.hours);
    if (problem) return setError(problem);
    setError('');
    try {
      await apiSend('/api/other-flights', 'POST', {
        date: `${of.date}T12:00:00Z`,
        departure_airport: of.dep || undefined,
        arrival_airport: of.arr || undefined,
        flight_hours: Number(of.hours),
        note: of.note || undefined,
      });
      setOf({ date: today(), dep: '', arr: '', hours: '', note: '' });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the flight');
    }
  }

  /** Open a row for editing. One with no flight time yet starts from FlightAware's figure. */
  function startEdit(o: OtherFlight) {
    const hours = o.flight_hours ?? o.airborne_hours;
    setEdit({
      id: o.id,
      date: new Date(o.date).toISOString().slice(0, 10),
      dep: o.departure_airport || '',
      arr: o.arrival_airport || '',
      hours: hours == null ? '' : String(hours),
      note: o.note || '',
    });
  }

  async function saveEdit() {
    if (!edit) return;
    const problem = flightTimeProblem(edit.hours);
    if (problem) return setError(problem);
    setError('');
    try {
      // Same rule as the flight form: an emptied nullable field has to reach the
      // API as a present, empty value. `|| undefined` drops the key, and a
      // dropped key means "leave it alone" — so clearing one saved as a no-op.
      await apiSend(`/api/other-flights/${edit.id}`, 'PATCH', {
        date: `${edit.date}T12:00:00Z`,
        departure_airport: edit.dep,
        arrival_airport: edit.arr,
        flight_hours: Number(edit.hours),
        note: edit.note,
      });
      setEdit(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the flight');
    }
  }

  async function delOther(id: string) {
    try {
      await apiSend(`/api/other-flights/${id}`, 'DELETE');
      setEdit((e) => (e?.id === id ? null : e));
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete');
    }
  }

  const unlogged = unloggedView(summary?.unlogged_flights);

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Co-owner & detections"
        subtitle="Is every flight FlightAware saw logged? And the co-owner's flying, for the aircraft's history."
      />
      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      {loading ? (
        <Spinner />
      ) : !summary ? (
        <EmptyState title="Could not load the page" hint="Reload the page, or check the API is running." />
      ) : (
        <div className="space-y-6">
          {/* ── 1: is every flight logged? ──────────────────────────────────── */}
          <Card className="p-5 sm:p-6">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Is every flight logged?</p>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3">
              <span className={`text-4xl font-bold tabular-nums ${unlogged.accent}`}>{unlogged.value}</span>
              <span className="min-w-0 text-sm font-medium text-white">{unlogged.hint}</span>
            </div>
            {pending.length > 0 && (
              <ul className="mt-3 space-y-1.5 border-t border-white/5 pt-3">
                {pending.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                    <span className="ident text-white">{p.departure_airport} → {p.arrival_airport}</span>
                    <span className="text-xs text-slate-500">
                      {formatInstant(p.departure_time)}
                      {p.flight_time_hours != null && Number(p.flight_time_hours) > 0 && ` · ${hrs(Number(p.flight_time_hours))} airborne`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/flightaware" className="mt-3 inline-block text-xs text-avio hover:underline">
              Aircraft tracking →
            </Link>
          </Card>

          {/* ── 2: the co-owner's flying ─────────────────────────────────────── */}
          <Section
            title="Co-owner flights"
            hint="Flown by the other owner: kept in the aircraft's history and BA Law's whole-aircraft sheet, out of your logbook and your taxes. A leg you flew together is one row in your flight log, at the whole leg's flight time."
          >
            <p className="mb-3 text-sm text-slate-400">
              <span className="tabular-nums text-white">{summary.other_hours.toFixed(1)} h</span> flight time ·{' '}
              {hoursHint(summary.other_flights, summary.other_flights_without_time)}
            </p>
            <form onSubmit={addOther} className="mb-4 grid gap-2 border-b border-white/5 pb-4 sm:grid-cols-[1.2fr_0.7fr_0.7fr_0.7fr_1.4fr_auto]">
              <input type="date" aria-label="Date" value={of.date} onChange={(e) => setOf((s) => ({ ...s, date: e.target.value }))} className={inputClass} />
              <input placeholder="From" aria-label="From" maxLength={4} value={of.dep} onChange={(e) => setOf((s) => ({ ...s, dep: code(e.target.value) }))} className={`${inputClass} ident`} />
              <input placeholder="To" aria-label="To" maxLength={4} value={of.arr} onChange={(e) => setOf((s) => ({ ...s, arr: code(e.target.value) }))} className={`${inputClass} ident`} />
              <input placeholder="Time" aria-label="Flight time (hrs)" required type="number" step="0.01" min="0.01" max="24" inputMode="decimal" value={of.hours} onChange={(e) => setOf((s) => ({ ...s, hours: e.target.value }))} className={inputClass} />
              <input placeholder="Note" aria-label="Note" value={of.note} onChange={(e) => setOf((s) => ({ ...s, note: e.target.value }))} className={inputClass} />
              <button className="btn-ghost text-sm">Add</button>
            </form>

            {others.length === 0 ? (
              <EmptyState title="No co-owner flights recorded" hint="Add the other owner's flights, or flag them from aircraft tracking." />
            ) : (
              <ul className="divide-y divide-white/5">
                {others.map((o) => (
                  <li key={o.id} className="-mx-2 rounded-lg px-2 py-3 transition hover:bg-white/[0.03]">
                    {edit?.id === o.id ? (
                      <div className="space-y-2">
                        <div className="grid gap-2 sm:grid-cols-[1.2fr_0.7fr_0.7fr_0.7fr_1.4fr_auto]">
                          <input type="date" aria-label="Date" value={edit.date} onChange={(e) => setEdit((s) => s && { ...s, date: e.target.value })} className={inputClass} />
                          <input placeholder="From" aria-label="From" maxLength={4} value={edit.dep} onChange={(e) => setEdit((s) => s && { ...s, dep: code(e.target.value) })} className={`${inputClass} ident`} />
                          <input placeholder="To" aria-label="To" maxLength={4} value={edit.arr} onChange={(e) => setEdit((s) => s && { ...s, arr: code(e.target.value) })} className={`${inputClass} ident`} />
                          <input placeholder="Time" aria-label="Flight time (hrs)" required type="number" step="0.01" min="0.01" max="24" inputMode="decimal" value={edit.hours} onChange={(e) => setEdit((s) => s && { ...s, hours: e.target.value })} className={inputClass} />
                          <input placeholder="Note" aria-label="Note" value={edit.note} onChange={(e) => setEdit((s) => s && { ...s, note: e.target.value })} className={inputClass} />
                          <div className="flex items-center gap-2">
                            <button onClick={saveEdit} className="btn-primary text-sm">Save</button>
                            <button onClick={() => setEdit(null)} className="btn-ghost text-sm">Cancel</button>
                          </div>
                        </div>
                        {o.flight_hours == null && o.airborne_hours != null && (
                          <p className="text-xs text-slate-500">
                            Filled in from FlightAware's measured airborne time, {hrs(o.airborne_hours)}. Change it to what was flown.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                        <div className="min-w-0">
                          <p className="ident text-sm text-white">
                            {o.departure_airport && o.arrival_airport ? `${o.departure_airport} → ${o.arrival_airport}` : 'Flight'}
                          </p>
                          <p className="break-words text-xs text-slate-500">
                            {formatCalendarDay(o.date)}
                            {` · ${distanceLabel(o)}`}
                            {o.note ? ` · ${o.note}` : ''}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          {o.flight_hours != null ? (
                            <span className="tabular-nums text-sm text-slate-300">{hrs(o.flight_hours)}</span>
                          ) : (
                            <button
                              onClick={() => startEdit(o)}
                              className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200 ring-1 ring-inset ring-amber-500/30"
                              title={o.airborne_hours != null ? `FlightAware measured ${hrs(o.airborne_hours)} airborne` : 'No measured figure to offer'}
                            >
                              no flight time
                            </button>
                          )}
                          <button onClick={() => startEdit(o)} className="text-xs text-slate-400 hover:text-white">Edit</button>
                          <button onClick={() => delOther(o.id)} className="text-slate-500 hover:text-red-300" aria-label="Remove">✕</button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}
